"use client"

import React from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import Icons, { Icon } from "../ui/Icon"
import {
	ShieldIcon,
	MapPin,
	Phone,
	Mail,
	MessageCircle,
	ExternalLink,
	Clock,
} from "lucide-react"
import { Container } from "../ui/Container"
import { getMediaUrl } from "@/utilities/getMediaUrl"
import { cn } from "@/lib/utils"

interface ContactInfoClientProps {
	siteSettings: any
	footer: any
}

export const ContactInfoClient: React.FC<ContactInfoClientProps> = ({
	siteSettings,
	footer,
}) => {
	const pathname = usePathname()

	const logo = siteSettings?.logo
	const logoUrl =
		typeof logo === "object" && logo?.url ? getMediaUrl(logo.url) : null
	const logoAlt =
		typeof logo === "object" && logo?.alt ? logo.alt : "A&N Construcciones"

	const title = "A&N Construcciones"

	// Create contact methods from siteSettings
	const contactMethods = []

	if (siteSettings?.address) {
		contactMethods.push({
			id: "address",
			icon: MapPin,
			title: "Dirección",
			value: siteSettings.address,
			description: "Visítanos en nuestra oficina",
			href: "#",
			color: "bg-blue-500",
		})
	}

	if (siteSettings?.phone) {
		contactMethods.push({
			id: "phone",
			icon: Phone,
			title: "Teléfono",
			value: siteSettings.phone,
			description: "Llamanos de lunes a viernes",
			href: `tel:${siteSettings.phone}`,
			color: "bg-green-500",
		})
	}

	if (siteSettings?.email) {
		contactMethods.push({
			id: "email",
			icon: Mail,
			title: "Correo Electrónico",
			value: siteSettings.email,
			description: "Escríbenos tu consulta",
			href: `mailto:${siteSettings.email}`,
			color: "bg-purple-500",
		})
	}

	if (siteSettings?.whatsapp) {
		const whatsappValue =
			siteSettings.whatsapp.startsWith("http") ||
			siteSettings.whatsapp.startsWith("wa.me")
				? "Mensaje Directo"
				: siteSettings.whatsapp
		contactMethods.push({
			id: "whatsapp",
			icon: MessageCircle,
			title: "WhatsApp",
			value: whatsappValue,
			description: "Escríbenos directamente",
			href: siteSettings.whatsapp,
			color: "bg-emerald-500",
		})
	}

	if (siteSettings?.businessHours) {
		contactMethods.push({
			id: "hours",
			icon: Clock,
			title: "Horario de Atención",
			value: siteSettings.businessHours,
			description: "Lunes a Viernes",
			href: "#",
			color: "bg-orange-500",
		})
	}

	return (
		<section className="bg-linear-to-br from-gray-800 via-gray-900 to-gray-800 text-white relative overflow-hidden">
			{/* <!-- Background Pattern --> */}
			<div className="absolute inset-0 opacity-5">
				<svg
					width="60"
					height="60"
					viewBox="0 0 60 60"
					xmlns="http://www.w3.org/2000/svg"
					className="w-full h-full"
					aria-hidden="true"
				>
					<title>Background Pattern</title>
					<g fill="none" fillRule="evenodd">
						<g fill="currentColor" fillOpacity="0.1">
							<circle cx="30" cy="30" r="1"></circle>
						</g>
					</g>
				</svg>
			</div>

			<Container className="relative z-10 container mx-auto px-4 py-16 lg:py-24">
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
					{/* <!-- Company Information --> */}
					<div className="lg:col-span-1">
						<div className="text-center lg:text-left">
							<div className="flex justify-center lg:justify-start items-center gap-4 mb-6">
								{logoUrl && (
									<div className="w-16 h-16 bg-primary/20 rounded-xl flex items-center justify-center">
										<Image
											src={logoUrl}
											alt={logoAlt}
											width={200}
											height={200}
											className="w-10 h-10"
										/>
									</div>
								)}
								<div>
									<h2 className="text-2xl lg:text-3xl font-bold text-primary mb-1">
										{title}
									</h2>
									<p className="text-gray-300 text-sm">
										Constructores de Confianza
									</p>
								</div>
							</div>

							<p className="text-gray-300 text-lg leading-relaxed mb-8">
								Transformamos tus ideas en realidad con más de 8 años de
								experiencia en el sector de la construcción y diseño
								arquitectónico.
							</p>

							{/*<!-- Quick Stats -->*/}
							<div className="grid grid-cols-3 gap-4 mb-8">
								{footer?.contactStats?.map((stat: any) => (
									<div
										key={stat.label}
										className="text-center bg-gray-700/50 rounded-lg p-4 backdrop-blur-sm"
									>
										<div className="text-xl font-bold text-primary mb-1">
											{stat.value}
										</div>
										<div className="text-xs text-gray-300">{stat.label}</div>
									</div>
								))}
							</div>

							{/*<!-- Primary CTA -->*/}
							<div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
								{siteSettings?.whatsapp && (
									<a
										href={siteSettings?.whatsapp}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all duration-300 transform hover:scale-105"
									>
										<Icons.whatsapp className="w-5 h-5 mr-2" />
										Consulta Rápida
									</a>
								)}
								{siteSettings?.email && (
									<a
										href={`mailto:${siteSettings?.email}`}
										className="inline-flex items-center justify-center px-6 py-3 border border-primary text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-colors duration-300"
									>
										<Icons.mail className="w-4 h-4 mr-2" />
										Enviar Email
									</a>
								)}
							</div>
						</div>
					</div>

					{/* <!-- Contact Methods --> */}
					<div className="lg:col-span-1">
						<h3 className="text-2xl font-bold mb-8 text-center lg:text-left">
							Métodos de Contacto
						</h3>
						<div className="space-y-4">
							{contactMethods.map((method) => (
								<div
									key={method.id}
									className="bg-gray-700/30 rounded-xl p-4 backdrop-blur-sm border border-gray-600/30 hover:border-primary/30 transition-all duration-300 group"
								>
									<div className="flex items-start gap-4">
										<div
											className={`w-12 h-12 ${method.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
										>
											<method.icon className="w-6 h-6 text-white" />
										</div>
										<div className="flex-1">
											<h4 className="font-semibold text-white mb-1">
												{method.title}
											</h4>
											<p className="text-gray-300 text-sm mb-1">
												{method.value}
											</p>
											<p className="text-gray-300 text-xs">
												{method.description}
											</p>
										</div>
										{method.href !== "#" && (
											<a
												href={method.href}
												target={
													method.href.startsWith("http") ? "_blank" : undefined
												}
												rel={
													method.href.startsWith("http")
														? "noopener noreferrer"
														: undefined
												}
												aria-label={`Abrir ${method.title} (enlace externo)`}
												className="text-primary hover:text-primary/80 transition-colors duration-200"
											>
												<ExternalLink className="w-4 h-4" />
											</a>
										)}
									</div>
								</div>
							))}
						</div>
					</div>

					{/*<!-- Navigation & Social -->*/}
					<div className="lg:col-span-1">
						{/*<!-- Navigation Menu -->*/}
						{footer?.quickLinks && (
							<div className="mb-12">
								<h3 className="text-2xl font-bold mb-6 text-center lg:text-left">
									Navegación Rápida
								</h3>
								<nav className="grid grid-cols-1 gap-3">
									{footer?.quickLinks?.map((link: any) => {
										const page =
											typeof link.page === "object" ? link.page : null
										const slug = page?.slug
										// Si no hay slug, saltar
										if (!slug) {
											return null
										}
										const href =
											slug === "home" || slug === "/" ? "/" : `/${slug}`
										const isActive =
											pathname === href ||
											(href !== "/" && pathname.startsWith(href))

										// Default icons by slug
										const defaultIcons: Record<string, string> = {
											home: "home",
											nosotros: "group",
											servicios: "briefcase",
											proyectos: "case",
											contacto: "mail",
										}
										const iconName =
											page.icon || defaultIcons[slug] || "chevron_right"

										return (
											<Link
												key={link.id}
												href={href}
												className={cn(
													"group flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300",
													isActive
														? "bg-primary/20 text-primary border border-primary/30"
														: "bg-gray-700/30 text-gray-300 border border-gray-600/30 hover:bg-gray-700/50 hover:text-white",
												)}
											>
												<Icon name={iconName} className="w-4 h-4" />
												<span className="font-medium">{page.title}</span>
												<Icons.chevron_right className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
											</Link>
										)
									})}
								</nav>
							</div>
						)}

						{/*<!-- Social Media -->*/}
						<div>
							{siteSettings?.socialLinks && (
								<div>
									<h3 className="text-2xl font-bold mb-6 text-center lg:text-left">
										Síguenos
									</h3>
									<div className="flex justify-center lg:justify-start gap-4">
										{siteSettings?.socialLinks?.map((social: any) => (
											<a
												key={social.id}
												href={social.url}
												target="_blank"
												rel="noopener noreferrer"
												aria-label={`Visitar ${social.platform}`}
												className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center hover:bg-primary transition-all duration-300 group transform hover:scale-110"
											>
												<Icon
													name={social.platform}
													className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors duration-200"
												/>
											</a>
										))}
									</div>
								</div>
							)}

							{/* <!-- Trust Badge --> */}
							{footer?.isVerified && (
								<div className="mt-8 p-4 bg-linear-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
									<div className="flex items-center gap-3">
										<ShieldIcon className="w-6 h-6 text-primary" />
										<div>
											<p className="text-sm font-semibold text-primary">
												Empresa Verificada
											</p>
											<p className="text-xs text-gray-400">
												Miembro certificado del sector construcción
											</p>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</Container>
		</section>
	)
}
